import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, ChevronDown, X, Grid3X3, Grid2X2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import ProductCard from '@/components/products/ProductCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function ProductsPage() {
  const { category } = useParams();
  const { categories, getProductsByCategory } = useApp();
  const [products, setProducts] = useState([]);
  const [sortBy, setSortBy] = useState('featured');
  const [gridCols, setGridCols] = useState(2);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');

  useEffect(() => {
    const filtered = getProductsByCategory(selectedCategory);
    let sorted = [...filtered];

    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // featured - keep original order
        break;
    }

    setProducts(sorted);
  }, [selectedCategory, sortBy, getProductsByCategory]);

  useEffect(() => {
    if (category) {
      setSelectedCategory(category);
    }
  }, [category]);

  const currentCategory = categories.find(c => c.id === selectedCategory);

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
  ];

  return (
    <div className="page-transition min-h-screen">
      {/* Header */}
      <div className="bg-gradient-burgundy-soft border-b border-border/30">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-sm text-silver font-sans tracking-wider uppercase mb-2 block">
              Our Collection
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              {currentCategory ? currentCategory.name : 'All Products'}
            </h1>
            {currentCategory && (
              <p className="text-muted-foreground font-body max-w-md mx-auto">
                {currentCategory.description}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-primary-light text-ivory'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === cat.id
                    ? 'bg-primary-light text-ivory'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat.name.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>

          {/* Sort & View Controls */}
          <div className="flex items-center gap-3">
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {sortOptions.find(o => o.value === sortBy)?.label}
                  </span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={sortBy === option.value ? 'bg-muted' : ''}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Grid Toggle */}
            <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setGridCols(2)}
                className={`p-1.5 rounded transition-colors ${
                  gridCols === 2 ? 'bg-card text-silver-light' : 'text-muted-foreground'
                }`}
                aria-label="2 column grid"
              >
                <Grid2X2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setGridCols(4)}
                className={`p-1.5 rounded transition-colors ${
                  gridCols === 4 ? 'bg-card text-silver-light' : 'text-muted-foreground'
                }`}
                aria-label="4 column grid"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">
          Showing {products.length} {products.length === 1 ? 'product' : 'products'}
        </p>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div
            className={`grid gap-4 md:gap-6 ${
              gridCols === 4
                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                : 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3'
            }`}
          >
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground font-body mb-4">
              No products found in this category.
            </p>
            <Link to="/products">
              <Button variant="silver">View All Products</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
